import { loadedAssets } from "@/loader";
import AssetLoader from "@/utils/asset-loader";
import { emojiMap, extractEmojis, getEmojiSprite } from "@/utils/emoij";
import {
  Actor,
  Color,
  Font,
  Label,
  SpriteSheet,
  vec,
  NineSlice,
  NineSliceConfig,
  NineSliceStretch,
} from "excalibur";

export class ChatBubble {
  private myNineSlice: any;
  private lineHeight: number;
  private emojiSize: number;
  private textFontSize: number;
  spriteSheet: any;
  previousChat: any = null;
  bubble: Actor;
  assetLoader: any;

  constructor(
    lineHeight: number = 24,
    emojiSize: number = 24,
    textFontSize: number = 13
  ) {
    this.lineHeight = lineHeight;
    this.emojiSize = emojiSize;
    this.textFontSize = textFontSize;

    this.assetLoader = new AssetLoader();

    this.myNineSlice = this.createNineSlice();
    this.spriteSheet = this.createSpriteSheet();
  }

  private createSpriteSheet() {
    const image = loadedAssets["emotes.png"];
    if (!image.isLoaded()) return;

    const spriteSheet = SpriteSheet.fromImageSource({
      image,
      grid: {
        rows: 10,
        columns: 17,
        spriteWidth: this.emojiSize,
        spriteHeight: this.emojiSize,
      },
    });

    return spriteSheet;
  }

  private async createNineSlice() {
    try {
      // Load all assets
      await this.assetLoader.loadImage("./public/gui/chat-bubble.png");
    } catch (error) {
      console.error("Error loading assets:", error);
    }
console.log(this.assetLoader.getAssetById("./public/gui/chat-bubble.png"))
    const myNineSliceConfig: NineSliceConfig = {
      width: 0,
      height: 35,
      source: loadedAssets["chat-bubble.png"],
      sourceConfig: {
        width: 18,
        height: 18,
        topMargin: 5,
        leftMargin: 5,
        bottomMargin: 5,
        rightMargin: 6,
      },
      destinationConfig: {
        drawCenter: true,
        horizontalStretch: NineSliceStretch.Stretch,
        verticalStretch: NineSliceStretch.TileFit,
      },
    };

    return new NineSlice(myNineSliceConfig);
  }

  public remove() {
    this.bubble.kill();
  }

  public displayText({
    text,
    isTyping,
    scene,
    bodyCenterX,
    offsetY,
  }: {
    text: string;
    isTyping: boolean | null;
    scene: any;
    bodyCenterX: number;
    offsetY: number;
  }): void {
    const totalWidth = this.getTextWidth(text);

    const chatBubble = this.createBubble(bodyCenterX - totalWidth / 2, offsetY);
    scene.addChild(chatBubble);

    this.myNineSlice.setTargetWidth(totalWidth + 10);

    const verticalAlignmentOffset =
      (this.lineHeight - Math.max(this.emojiSize, this.textFontSize)) / 2;

    let offsetX = 5;

    const parts = extractEmojis(text);
    parts?.forEach((part: any) => {
      if (emojiMap[part]) {
        const emojiSprite = getEmojiSprite(part, this.spriteSheet);
        if (emojiSprite) {
          const emoijActor = this.addEmoij(
            emojiSprite,
            offsetX,
            verticalAlignmentOffset + 5
          );

          chatBubble.addChild(emoijActor);
          offsetX += this.emojiSize;
        }
      } else {
        const textActor = this.addText(
          part,
          offsetX + 2,
          verticalAlignmentOffset + 10
        );

        chatBubble.addChild(textActor);

        const textWidth = textActor.font.measureText(part).width;
        offsetX += textWidth + 5;
      }
    });

    this.bubble = chatBubble;

    this.actions.delay(2000).callMethod(() => {
      chatBubble.actions.fade(0, 100);
    });
  }

  private addEmoij(emojiSprite: any, offsetX: number, offsetY: number) {
    const emojiActor = new Actor({
      pos: vec(offsetX, offsetY),
      anchor: vec(0, 0),
    });

    emojiActor.graphics.use(emojiSprite);

    return emojiActor;
  }

  private addText(text: string, offsetX: number, offsetY: number) {
    const textActor = new Label({
      text,
      pos: vec(offsetX, offsetY),
      color: Color.White,
      anchor: vec(0, 0),
      font: new Font({
        family: "arial",
        size: this.textFontSize,
        shadow: {
          blur: 2,
          offset: vec(2, 2),
          color: Color.Black,
        },
      }),
    });

    return textActor;
  }

  private createBubble(offsetX, offsetY) {
    const chatBubble = new Actor({
      pos: vec(offsetX - 8, offsetY - 10),
      anchor: vec(0, 0),
    });

    chatBubble.graphics.use(this.myNineSlice);

    return chatBubble;
  }

  private getTextWidth(text: string) {
    const parts = extractEmojis(text);

    let totalWidth = 0;
    parts?.forEach((part) => {
      if (emojiMap[part]) {
        totalWidth += this.emojiSize;
      } else {
        const tempLabel = new Label({
          text: part,
          font: new Font({
            family: "arial",
            size: this.textFontSize,
          }),
        });
        totalWidth += tempLabel.font.measureText(part).width + 5;
      }
    });

    return totalWidth;
  }

  private actions: any = {
    delay: (time: number) => {
      return {
        callMethod: (fn: () => void) => {
          setTimeout(fn, time);
        },
      };
    },
  };
}

